import { toast } from "@/components/ui/toast";
import { useRef } from "react";
import { type AnyVariables, type DocumentInput, useMutation } from "urql";

type TCsvExportItem =
  | { __typename: "CSVExportResponse"; url: string }
  | { __typename: "OperationInfo"; messages: { message: string }[] }
  | null;

interface UseCsvExportOptions<TData, TVariables extends AnyVariables> {
  mutation: DocumentInput<TData, TVariables>;
  /** Extracts the response union items from the mutation result data. */
  getResponses: (data: TData) => TCsvExportItem[];
  /** Called at export time to produce the download filename. */
  getFilename: () => string;
}

/**
 * Shared hook for CSV export via presigned S3 URL.
 *
 * Handles the urql mutation, {@link TCsvExportItem} response discrimination,
 * toast error reporting, and anchor-based file download. Returns a
 * `DownloadAnchor` element that must be rendered in the consuming component.
 *
 * @example
 * const { triggerExport, isExporting, DownloadAnchor } = useCsvExport({
 *   mutation: ExportShareVoiceTableCsv,
 *   getResponses: (data) => data.exportShareVoiceTableCsv ?? [],
 *   getFilename: () => `share-of-voice-${new Date().toISOString()}-export.csv`,
 * });
 */
export const useCsvExport = <TData, TVariables extends AnyVariables>({
  mutation,
  getResponses,
  getFilename,
}: UseCsvExportOptions<TData, TVariables>) => {
  const downloadRef = useRef<HTMLAnchorElement>(null);
  const [{ fetching: isExporting }, executeMutation] = useMutation(mutation);

  const triggerExport = async (variables: TVariables) => {
    const result = await executeMutation(variables);

    if (result.error) {
      toast.error(result.error.message);
      return;
    }

    if (!result.data) return;

    const responses = getResponses(result.data);
    const csvResponse = responses.find(
      (r) => r?.__typename === "CSVExportResponse",
    );

    if (csvResponse?.__typename === "CSVExportResponse") {
      const anchor = downloadRef.current;
      if (anchor) {
        anchor.href = csvResponse.url;
        anchor.download = getFilename();
        anchor.click();
        anchor.href = "about:blank";
      }
    } else {
      const operationInfo = responses.find(
        (r) => r?.__typename === "OperationInfo",
      );
      const message =
        operationInfo?.__typename === "OperationInfo"
          ? operationInfo.messages.map((m) => m.message).join(" ")
          : "Export failed";
      toast.error(message);
    }
  };

  const DownloadAnchor = (
    <a
      ref={downloadRef}
      href="about:blank"
      style={{ display: "none" }}
      tabIndex={-1}
      aria-label="Download export file"
    >
      Download
    </a>
  );

  return { triggerExport, isExporting, DownloadAnchor };
};
